import type { SessionData } from '@pickleballinc/lib/session';
import type { ReactNode } from 'react';
import { z } from 'zod';

/**
 * The kind of engagement tracked by `ImpressionWrapper` and forwarded to BE's `/v2/engagement` endpoint.
 * Values are intentionally mirroring the BE proto enum integers.
 */
export enum ActivityType {
	VIEW = 1,
	CLICK = 2
}

/** Shape of the response from the per-app `/api/v2/impressions/*` Next.js API routes. */
export interface ImpressionRes {
	message: string;
	statusCode?: number;
}

export interface ImpressionContextInterface {
	userId: string;
	/**
	 * Forwarded from the provider's `isLocalhost` prop. `true` when impression tracking should short-circuit
	 * in this environment. `undefined` when the consumer didn't pass the prop — treated as falsy by callers.
	 */
	isLocalhost?: boolean;
}

export interface ImpressionProviderProps {
	children: ReactNode;
	/** Server-side session info. The provider only reads `session.user.uuid`. */
	session?: SessionData | null;
	/**
	 * When `true`, the provider short-circuits its anon-UUID / merge-on-login flow. Pass your app's
	 * `IS_LOCALHOST` constant (typically derived from an env-var URL) so impression tracking is disabled in
	 * the same environments the rest of the app treats as local. When omitted, treated as falsy — tracking
	 * runs as normal.
	 */
	isLocalhost?: boolean;
}

export interface ImpressionWrapperProps {
	/** Numeric `SystemModuleType` (or any per-app enum) identifying the engagement context. */
	moduleFor: number;
	/** UUID identifying the specific record (tournament, ad, etc.) being engaged with. */
	moduleId: string;
	children: ReactNode;
	className?: string;
	disableClickTrack?: boolean;
	disableImpressionTrack?: boolean;
	/** IntersectionObserver threshold (0..1) above which a VIEW counts. Defaults to 0.5 at the call site. */
	impressionThreshold?: number;
}

/**
 * Build the zod schema used by the per-app `addImpression` API route to validate inbound bodies. Each app
 * passes its own set of allowed `module_for` values (typically `Object.values(SystemModuleType).filter`-ed
 * to numbers) so the shared schema stays decoupled from any specific app's enum.
 *
 * Returns a Zod object schema; consumers can `.parse(body)` to validate or `.infer` for the body type.
 */
export const createImpressionBodySchema = (allowedModuleFors: readonly number[]) =>
	z.object({
		user_key: z.string().uuid(),
		activity_type: z.nativeEnum(ActivityType),
		module_for: z.number().refine((v) => allowedModuleFors.includes(v), {
			message: `module_for must be one of: ${allowedModuleFors.join(', ')}`
		}),
		module_uuid: z.string().min(1)
	});

/** Inferred body type from any schema produced by `createImpressionBodySchema`. */
export type AddImpressionBody = z.infer<ReturnType<typeof createImpressionBodySchema>>;
